// how.fm Trainer bridge.
//
// When VTA runs as a tab inside the how.fm Trainer (the Windows app on the
// packing station), the Trainer exposes `window.howfmTrainer` to this origin
// only:
//   - the SwiftLOG packing context it reads from its own tabs, pushed on every
//     change together with the events it derived (order-started, all-packed,
//     weight-entered, …),
//   - hardware triggers such as the USB buzzer ({ kind: "proof", source }),
//   - a local proof archive (`saveProof`) on the station's disk.
// In a normal browser the API is absent and every method degrades to a no-op,
// so the rest of the app never needs to know where it runs.

function safeCall(fn, payload) {
  try {
    fn(payload);
  } catch (_) {
    /* one bad listener must not break the others */
  }
}

export class TrainerBridge {
  constructor(api = typeof window !== "undefined" ? window.howfmTrainer : null) {
    this.api = api && typeof api === "object" ? api : null;
    this.latest = null; // { context, events, ts }
    this._contextListeners = new Set();
    this._triggerListeners = new Set();
    if (!this.api) return;
    if (typeof this.api.onContext === "function") {
      this.api.onContext((payload) => this._publishContext(payload));
    }
    if (typeof this.api.onTrigger === "function") {
      this.api.onTrigger((trigger) => {
        for (const fn of this._triggerListeners) safeCall(fn, trigger);
      });
    }
    if (typeof this.api.getContext === "function") {
      Promise.resolve(this.api.getContext())
        .then((payload) => {
          // A push that arrived meanwhile is newer than this snapshot.
          if (payload && !this.latest) this._publishContext({ ...payload, events: [] });
        })
        .catch(() => {});
    }
  }

  get available() {
    return !!this.api;
  }

  _publishContext(payload) {
    if (!payload || typeof payload !== "object") return;
    this.latest = payload;
    for (const fn of this._contextListeners) safeCall(fn, payload);
  }

  onContext(fn) {
    if (typeof fn !== "function") return () => {};
    this._contextListeners.add(fn);
    return () => this._contextListeners.delete(fn);
  }

  onTrigger(fn) {
    if (typeof fn !== "function") return () => {};
    this._triggerListeners.add(fn);
    return () => this._triggerListeners.delete(fn);
  }

  async saveProof(record) {
    if (!this.api || typeof this.api.saveProof !== "function") {
      return { ok: false, reason: "unavailable" };
    }
    try {
      return (await this.api.saveProof(record)) || { ok: false, reason: "no answer" };
    } catch (e) {
      return { ok: false, reason: e?.message || String(e) };
    }
  }
}
